import { queryOptions } from '@tanstack/react-query';

import type { IConnectivityStatus } from '@/service/vpc/types/IConnectivityStatus';
import type { InvitationStatusType } from '@/service/vpc/types/IFleetInvitations';
import type { ILastSeen } from '@/service/vpc/types/ILastSeen';
import type { IChassisIdentifier, IUnit } from '@/service/vpc/types/IUnit';
import type { IWifiNetworkRow } from '@/service/vpc/types/IWifiConfiguration';

import { queryClient } from '@/config/QueryConfig';
import { vpcService } from '@/service/vpc/vpcService';

// Centralized query key strings to ensure consistency across the app
export const queryKeys = {
  getFleetAccounts: 'getFleetAccounts',
  getFleetAccountRoles: 'getFleetAccountRoles',
  getAvailableRoles: 'getAvailableRoles',
  getUserAuthorizations: 'getUserAuthorizations',
  getUnitsWithFBB: 'getUnitsWithFBB',
  getUnitsLastSeen: 'getUnitsLastSeen',
  getUnitsConnectivityStatus: 'getUnitsConnectivityStatus',
  getFleetUnits: 'getFleetUnits',
  getFleetUnitsCount: 'getFleetUnitsCount',
  getLatestOperatingHours: 'getLatestOperatingHours',
  getCombinedCampaigns: 'getCombinedCampaigns',
  getFleetAccountEndUsers: 'getFleetAccountEndUsers',
  getFleetAccountEndUserById: 'getFleetAccountEndUserById',
  getAssetPositions: 'getAssetPositions',
  getUnitById: 'getUnitById',
  getFleets: 'getFleets',
  getFleetRoles: 'getFleetRoles',
  getAvailableFleetRoles: 'getAvailableFleetRoles',
  getIndividualProducts: 'getIndividualProducts',
  getEndUser: 'getEndUser',
  getEndCustomer: 'getEndCustomer',
  getInvitations: 'getInvitations',
  getInvitationById: 'getInvitationById',
  getLatestActiveState: 'getLatestActiveState',
  getServiceProtocolByKey: 'getServiceProtocolByKey',
  getServiceProtocols: 'getServiceProtocols',
  getServices: 'getServices',
  getHandbooks: 'getHandbooks',
  getMemberRoles: 'getMemberRoles',
  getFuelConsumptionByMonth: 'getFuelConsumptionByMonth',
  getWifiStatusHistory: 'getWifiStatusHistory',
  getWifiConfig: 'getWifiConfig',
  getWifiErrorHistory: 'getWifiErrorHistory',
} as const;

export const fleetAccountsQueryOptions = () =>
  queryOptions({
    queryKey: [queryKeys.getFleetAccounts],
    queryFn: async () => vpcService.FleetAccount.getFleetAccounts(),
  });

export const getAvailableRolesQueryOptions = () =>
  queryOptions({
    queryKey: [queryKeys.getAvailableRoles],
    queryFn: async () => vpcService.FleetAccount.getAvailableRoles(),
  });

export const getFleetAccountRolesQueryOptions = ({ fleetAccountId }: { fleetAccountId: string }) =>
  queryOptions({
    queryKey: [queryKeys.getFleetAccountRoles, fleetAccountId],
    queryFn: () => vpcService.FleetAccount.getFleetAccountRoles({ fleetAccountId }),
    enabled: !!fleetAccountId,
  });

export const getUserAuthorizationsQueryOptions = () =>
  queryOptions({
    queryKey: [queryKeys.getUserAuthorizations],
    queryFn: () => vpcService.FleetAccount.getUserAuthorizations(),
  });

export const allUnitsQueryOptions = () =>
  queryOptions({
    queryKey: [queryKeys.getUnitsWithFBB],
    queryFn: async () => vpcService.Units.getUnitsWithFBB(),
  });

export const getAssetPositionsQueryOptions = ({
  endCustomerUnitIds,
  year,
  month,
}: {
  endCustomerUnitIds: number[];
  year: string;
  month: string;
}) =>
  queryOptions({
    queryKey: [queryKeys.getAssetPositions, endCustomerUnitIds, year, month],
    queryFn: async () =>
      vpcService.PowerSolutions.getAssetPositions({
        endCustomerUnitIds,
        year,
        month,
      }),
    enabled: endCustomerUnitIds.length > 0,
  });

export const getLatestOperatingHoursQueryOptions = ({ endcustomerunitids }: { endcustomerunitids: number[] }) =>
  queryOptions({
    queryKey: [queryKeys.getLatestOperatingHours, endcustomerunitids],
    queryFn: async () =>
      vpcService.PowerSolutions.getLatestOperatingHours({
        endcustomerunitids,
      }),
    enabled: endcustomerunitids.length > 0,
  });

export const getCombinedCampaignsQueryOptions = ({
  endCustomerUnitIds,
  serialNumbers,
}: {
  endCustomerUnitIds: string[];
  serialNumbers: string[];
}) =>
  queryOptions({
    queryKey: [queryKeys.getCombinedCampaigns, endCustomerUnitIds, serialNumbers],
    queryFn: async () =>
      vpcService.Campaigns.getCombinedCampaigns({
        endCustomerUnitIds,
        serialNumbers,
      }),
    enabled: serialNumbers.length > 0,
  });

export const getFleetAccountEndUsersQueryOptions = ({ fleetAccountId }: { fleetAccountId: string }) =>
  queryOptions({
    queryKey: [queryKeys.getFleetAccountEndUsers, fleetAccountId],
    queryFn: async ({ signal }) => vpcService.FleetAccount.getFleetAccountEndUsers({ fleetAccountId, signal }),
    enabled: !!fleetAccountId,
  });

export const getFleetAccountEndUserByIdQueryOptions = ({
  fleetAccountId,
  endUserId,
}: {
  fleetAccountId: string;
  endUserId: string;
}) =>
  queryOptions({
    queryKey: [queryKeys.getFleetAccountEndUserById, fleetAccountId, endUserId],
    queryFn: async ({ signal }) =>
      vpcService.FleetAccount.getFleetAccountEndUserById({ fleetAccountId, endUserId, signal }),
    enabled: !!fleetAccountId && !!endUserId,
  });

export const unitByIdQueryOptions = ({ endCustomerUnitId }: { endCustomerUnitId: string }) =>
  queryOptions({
    queryKey: [queryKeys.getUnitById, endCustomerUnitId],
    queryFn: async () => vpcService.Units.getUnitById({ endCustomerUnitId }),
    enabled: !!endCustomerUnitId,
  });

export const getIndividualProductsQueryOptions = ({ serialNumbers }: { serialNumbers: string[] }) =>
  queryOptions({
    queryKey: [queryKeys.getIndividualProducts, serialNumbers],
    queryFn: async () =>
      vpcService.IndividualProducts.getIndividualProducts({
        serialNumbers,
      }),
    enabled: serialNumbers.length > 0,
  });

export const getEndUserQueryOptions = () =>
  queryOptions({
    queryKey: [queryKeys.getEndUser],
    queryFn: async () => vpcService.FleetAccount.getEndUser(),
  });

export const getEndCustomerQueryOptions = () =>
  queryOptions({
    queryKey: [queryKeys.getEndCustomer],
    queryFn: async ({ signal }) => vpcService.EndCustomer.getEndCustomer({ signal }),
    // ECM master data changes rarely; one fetch serves every activation modal on the page.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

export type LastSeenMap = Record<number, ILastSeen>;

export const getUnitsLastSeenQueryOptions = ({ endCustomerUnitIds }: { endCustomerUnitIds: (number | string)[] }) =>
  queryOptions({
    queryKey: [queryKeys.getUnitsLastSeen],
    queryFn: async (): Promise<LastSeenMap> => {
      const data = await vpcService.UnitStatuses.getUnitsLastSeen({ endCustomerUnitIds });
      const map: LastSeenMap = {};
      for (const item of data) {
        map[item.endCustomerUnitId] = item;
      }
      return map;
    },
    enabled: endCustomerUnitIds.length > 0,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 3 * 60 * 1000,
  });

export type ConnectivityStatusMap = Record<number, IConnectivityStatus>;

export const getUnitsConnectivityStatusQueryOptions = ({
  endCustomerUnitIds,
}: {
  endCustomerUnitIds: (number | string)[];
}) =>
  queryOptions({
    queryKey: [queryKeys.getUnitsConnectivityStatus],
    queryFn: async (): Promise<ConnectivityStatusMap> => {
      const data = await vpcService.UnitStatuses.getUnitsConnectivityStatus({ endCustomerUnitIds });
      const map: ConnectivityStatusMap = {};
      for (const item of data) {
        map[item.endCustomerUnitId] = item;
      }
      return map;
    },
    enabled: endCustomerUnitIds.length > 0,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 3 * 60 * 1000,
  });

export const mergeLastSeenToCache = (units: Array<{ endCustomerUnitId: number; lastSeen?: ILastSeen | null }>) => {
  const existing = queryClient.getQueryData<LastSeenMap>([queryKeys.getUnitsLastSeen]) ?? {};
  const updated = { ...existing };
  let changed = false;
  for (const unit of units) {
    if (unit.lastSeen) {
      updated[unit.endCustomerUnitId] = unit.lastSeen;
      changed = true;
    }
  }
  if (changed) {
    queryClient.setQueryData([queryKeys.getUnitsLastSeen], updated);
  }
};

// Show a just-registered unit right away instead of waiting on the units refetch (AB#1640822).
export const addRegisteredUnitToCache = (unit: IUnit | null | undefined) => {
  if (!unit) return;
  queryClient.setQueryData<IUnit[]>([queryKeys.getUnitsWithFBB], (units) =>
    units ? [...units.filter((u) => u.endCustomerUnitId !== unit.endCustomerUnitId), unit] : undefined
  );
};

export const getFleetInvitationsQueryOptions = ({
  fleetAccountId,
  status,
  pageNumber,
  pageSize,
}: {
  fleetAccountId: string;
  status: InvitationStatusType;
  pageNumber: number;
  pageSize: number;
}) =>
  queryOptions({
    queryKey: [queryKeys.getInvitations, { fleetAccountId, status, pageNumber, pageSize }],
    queryFn: async () =>
      vpcService.Invitations.getInvitations({
        fleetAccountId,
        status,
        pageNumber,
        pageSize,
      }),
  });

export const getInvitationByIdQueryOptions = ({ invitationId, enabled }: { invitationId: string; enabled: boolean }) =>
  queryOptions({
    queryKey: [queryKeys.getInvitationById, invitationId],
    queryFn: async () => vpcService.Invitations.getInvitationById({ invitationId }),
    enabled: enabled,
  });

export const getLatestActiveStateQueryOptions = ({
  chassisIds,
  pageSize,
  pageNumber,
}: {
  chassisIds: string;
  pageSize: number;
  pageNumber: number;
}) =>
  queryOptions({
    queryKey: [queryKeys.getLatestActiveState, chassisIds, pageSize, pageNumber],
    queryFn: async () =>
      vpcService.UnitStatuses.getLatestActiveState({
        chassisIds,
        pageSize,
        pageNumber,
      }),
  });

export const getFleetsQueryOptions = ({ fleetAccountId }: { fleetAccountId: string }) =>
  queryOptions({
    queryKey: [queryKeys.getFleets, fleetAccountId],
    queryFn: () => vpcService.Fleet.getFleets({ fleetAccountId }),
    enabled: !!fleetAccountId,
  });

export const getAvailableFleetRolesQueryOptions = () =>
  queryOptions({
    queryKey: [queryKeys.getAvailableFleetRoles],
    queryFn: () => vpcService.Fleet.getAvailableFleetRoles(),
  });

export const getFleetRolesQueryOptions = ({ fleetId }: { fleetId: string }) =>
  queryOptions({
    queryKey: [queryKeys.getFleetRoles, fleetId],
    queryFn: () => vpcService.Fleet.getFleetRoles({ fleetId }),
    enabled: !!fleetId,
  });

export const getAllFleetUnitsQueryOptions = ({ fleetId }: { fleetId: string }) =>
  queryOptions({
    queryKey: [queryKeys.getFleetUnits, fleetId],
    queryFn: async () => vpcService.Fleet.getFleetUnits({ fleetId }),
    enabled: !!fleetId,
  });

export const getFleetsUnitsQueryOptions = ({ fleetAccountId }: { fleetAccountId: string }) =>
  queryOptions({
    queryKey: [queryKeys.getFleetUnitsCount, fleetAccountId],
    queryFn: async () => vpcService.Fleet.getFleetsUnits({ fleetAccountId }),
    enabled: !!fleetAccountId,
  });

export const getFleetUnitsCountQueryOptions = ({ fleetId }: { fleetId: string }) =>
  queryOptions({
    queryKey: [queryKeys.getFleetUnitsCount, fleetId],
    queryFn: async () => vpcService.Fleet.getFleetUnitsCount({ fleetId }),
    enabled: !!fleetId,
  });

export const getFuelConsumptionByMonthQueryOptions = ({
  chassisIds,
  year,
  month,
}: {
  chassisIds: string;
  year: number;
  month: string;
}) =>
  queryOptions({
    queryKey: [queryKeys.getFuelConsumptionByMonth, chassisIds, year, month],
    queryFn: () =>
      vpcService.PowerSolutions.getFuelConsumptionByMonth({
        chassisIds,
        year,
        month,
      }),
    enabled: chassisIds.length > 0,
  });

export const getServiceProtocolsQueryOptions = ({ chassisIdentifiers }: { chassisIdentifiers: IChassisIdentifier[] }) =>
  queryOptions({
    queryKey: [queryKeys.getServiceProtocols, chassisIdentifiers],
    queryFn: () =>
      vpcService.Services.getServiceProtocols({
        chassisIdentifiers,
      }),
    enabled: chassisIdentifiers.length > 0,
  });

export const getServiceProtocolByKeyQueryOptions = ({ protocolKey }: { protocolKey: string }) =>
  queryOptions({
    queryKey: [queryKeys.getServiceProtocolByKey, protocolKey],
    queryFn: () => vpcService.Services.getServiceProtocolByKey({ protocolKey }),
    enabled: !!protocolKey,
  });

export const getServicesQueryOptions = ({
  endCustomerUnitId,
  fleetAccountId,
}: {
  endCustomerUnitId: string;
  fleetAccountId: string;
}) =>
  queryOptions({
    queryKey: [queryKeys.getServices, endCustomerUnitId, fleetAccountId],
    queryFn: () =>
      vpcService.Services.getServices({
        endCustomerUnitId,
        fleetAccountId,
      }),
    enabled: !!endCustomerUnitId && !!fleetAccountId,
  });

export const getPublicationsQueryOptions = ({
  endCustomerUnitId,
  serialNumbers,
}: {
  endCustomerUnitId: string;
  serialNumbers?: string[];
}) =>
  queryOptions({
    queryKey: [queryKeys.getHandbooks, endCustomerUnitId, serialNumbers],
    queryFn: () => vpcService.Publications.getHandbooks({ endCustomerUnitId, serialNumbers }),
    enabled: !!endCustomerUnitId,
  });

export const getWifiConfigurationQueryOptions = ({ chassisId }: { chassisId?: string }) =>
  queryOptions({
    queryKey: [queryKeys.getWifiConfig, chassisId],
    queryFn: () => Promise.resolve([] as IWifiNetworkRow[]),
    enabled: false, // Data is set via queryClient.setQueryData from polling
  });

export const getWifiErrorHistoryQueryOptions = ({ chassisId }: { chassisId?: string }) =>
  queryOptions({
    queryKey: [queryKeys.getWifiErrorHistory, chassisId],
    queryFn: () => vpcService.Services.getWifiErrorHistory({ chassisId: chassisId! }),
    enabled: false,
  });
